/**
 * API client for the Agent Arena (RFC-0702).
 *
 * The Arena backend is a SEPARATE service (services/agent-arena-api), so calls
 * target its absolute origin (VITE_ARENA_API_URL). We still route through
 * apiFetch/apiRequest, which pass absolute URLs through unchanged while
 * attaching the Supabase JWT + credentials — the arena-api verifies the same
 * GoTrue secret, so the token works across services.
 */

import { apiRequest, getAuthToken } from './client'

const ARENA_BASE = import.meta.env.VITE_ARENA_API_URL || 'http://localhost:8080'

function arenaUrl(path: string): string {
  return `${ARENA_BASE}${path}`
}

/** A JSON value — the shape the arena API returns for free-form bags
 * (outputs/usage/input). Avoids `unknown`, which the repo's lint:types forbids. */
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue }

export type ModelDimension = 'image' | 'llm_family' | 'video'
export type OutputKind = 'image' | 'script' | 'video'

export interface ArenaInputField {
  id: string
  label: string
  type: string
  required: boolean
  default: JsonValue
  options?: string[]
}

export interface ArenaAgent {
  agent_type: string
  label: string
  model_dimension: ModelDimension
  models: string[]
  // Full list the picker renders; model_support says which are enabled per mode
  // (both null for non-video agents — then all `models` are selectable).
  model_choices?: string[]
  model_support?: Record<string, string[]> | null
  default_mode?: string | null
  input_fields: ArenaInputField[]
  output_kind: OutputKind
}

export interface ArenaResult {
  id: string
  sub_index: number
  model: string
  status: string // pending | running | success | failed
  outputs: Record<string, JsonValue>
  output_key: string | null
  usage: Record<string, JsonValue>
  latency_ms: number | null
  scores: JsonValue[]
  error: string | null
  warnings: JsonValue[]
}

export interface ArenaRunSummary {
  id: string
  agent_type: string
  model_dimension: ModelDimension
  models: string[]
  status: string // running | done | partial | failed | cancelled
  winner_model: string | null
  blind: boolean
  created_at: string
}

export interface ArenaRunDetail {
  run: ArenaRunSummary & { input: Record<string, JsonValue>; user_id: string }
  results: ArenaResult[]
}

export interface ArenaRating {
  model: string
  win_rate: number | null
  wins: number
  losses: number
  ties: number
  n_comparisons: number
}

export interface CreateArenaRunBody {
  agent_type: string
  models: string[]
  input: Record<string, JsonValue>
  blind: boolean
}

export interface CreateArenaRunResponse {
  run_id: string
  status: string
  results: { id: string; model: string; status: string }[]
}

export interface VoteBody {
  winner_result_id: string
  loser_result_id: string
  tie?: boolean
  blind?: boolean
  idempotency_key: string
}

export async function listArenaAgents(): Promise<{ agents: ArenaAgent[] }> {
  return apiRequest(arenaUrl('/arena/agents'))
}

export interface ArenaStyleOption {
  value: string
  label: string
}

/** Styles the arena can actually resolve (so the UI only offers supported ones). */
export async function listArenaStyles(): Promise<{ styles: ArenaStyleOption[] }> {
  return apiRequest(arenaUrl('/arena/styles'))
}

export async function createArenaRun(body: CreateArenaRunBody): Promise<CreateArenaRunResponse> {
  return apiRequest(arenaUrl('/arena/runs'), { method: 'POST', body: JSON.stringify(body) })
}

export async function listArenaRuns(limit = 20, offset = 0): Promise<{ runs: ArenaRunSummary[] }> {
  return apiRequest(arenaUrl(`/arena/runs?limit=${limit}&offset=${offset}`))
}

export async function getArenaRun(runId: string): Promise<ArenaRunDetail> {
  return apiRequest(arenaUrl(`/arena/runs/${runId}`))
}

export async function voteArena(
  runId: string,
  body: VoteBody,
): Promise<{ ok: boolean; rating: Record<string, JsonValue> }> {
  return apiRequest(arenaUrl(`/arena/runs/${runId}/vote`), {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function getLeaderboard(
  agentType: string,
): Promise<{ agent_type: string; ratings: ArenaRating[] }> {
  return apiRequest(arenaUrl(`/arena/leaderboard/${agentType}`))
}

/** Fetch an image result's artifact (auth'd) and return an object URL. */
export async function fetchArtifactObjectUrl(resultId: string): Promise<string> {
  // In prod the endpoint 307s to a GCS signed URL, and fetch follows the redirect.
  // This MUST stay a "simple" cross-origin request: any non-safelisted header
  // survives the redirect to GCS and forces a CORS preflight that the bucket
  // rejects (its CORS allows no custom request headers) → the fetch fails and the
  // artifact shows "unavailable". apiFetch always adds `Content-Type: application/
  // json` (non-simple), so bypass it and send ONLY the Bearer token — it auths the
  // first hop and is stripped on the cross-origin redirect (the signed URL is
  // self-authenticating). credentials omitted so the `*`-CORS GCS response reads.
  const token = await getAuthToken()
  const res = await fetch(arenaUrl(`/arena/results/${resultId}/artifact`), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'omit',
  })
  if (!res.ok) throw new Error(`artifact ${res.status}`)
  return URL.createObjectURL(await res.blob())
}
